import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import './ModuleBar.css'

/**
 * Bara de module (`#page-header-nav`), cu cele doua sageti de derulare.
 *
 * -----------------------------------------------------------------------------
 * CE SE REPARA
 * -----------------------------------------------------------------------------
 *
 * Proactivitee are paisprezece intrari si un buton de ecran intreg; Inventoree
 * si Softsee cate cincisprezece. Peste o anumita latime de fereastra ultimele
 * ies din ecran, si pana acum NIMIC nu arata ca mai sunt acolo: `app.css` da
 * barii `overflow-x: auto`, dar ii stinge si bara de derulare
 * (`scrollbar-width: none`). Deci portalul derula deja, in tacere.
 *
 * Asa ca nu se schimba felul in care bara se poarta - se adauga semnul care
 * lipsea. Vezi `ModuleBar.css` pentru de ce sagetile sunt `<li>`-uri lipicioase
 * de latime zero si nu un invelis in jurul barei.
 *
 * -----------------------------------------------------------------------------
 * CINE AJUNGE LA INTRARILE DIN AFARA ECRANULUI, SI CUM
 * -----------------------------------------------------------------------------
 *
 *   - cu mouse-ul: sagetile, si rotita (se preface in derulare pe orizontala
 *     cat timp mai e unde sa mearga - la capat nu se mai opreste pagina din
 *     derulat, ca sa nu ramana rotita blocata deasupra barii);
 *   - cu degetul: derularea tactila, care mergea si inainte;
 *   - cu tastatura: Tab, care mergea si el - browserul aduce in ecran legatura
 *     focalizata. Singurul lucru care lipsea era sa nu se opreasca FIX SUB
 *     sageata, si aia o rezolva `scroll-padding` din foaie.
 *
 * Sagetile sunt in plus pentru mouse, deci `aria-hidden` si scoase din drumul
 * lui Tab: tot ce fac ele se poate face si fara ele, iar doua butoane in plus
 * anuntate intr-un cititor de ecran ar fi doua opriri fara rost intr-o lista de
 * saisprezece legaturi.
 */

const ARROW = 46

/** Cat se muta la o apasare: aproape o fereastra, dar nu chiar - ramane un reper. */
const STEP = 0.8

export interface BarEnds {
  left: boolean
  right: boolean
}

/**
 * Ce sageti trebuie aprinse. Pur, ca sa se poata proba fara browser.
 *
 * Pragul de un pixel nu e decorativ: latimile ies fractionare (`zoom`-ul
 * browserului, `devicePixelRatio`), iar `scrollWidth` e rotunjit - fara el, o
 * bara care incape perfect ar aprinde sageata din dreapta pe jumatate din
 * ecrane.
 */
export function barEnds(scrollLeft: number, scrollWidth: number, clientWidth: number): BarEnds {
  const max = scrollWidth - clientWidth
  if (max <= 1) return { left: false, right: false }
  return { left: scrollLeft > 1, right: scrollLeft < max - 1 }
}

/**
 * Unde trebuie dus `scrollLeft` ca intrarea sa se vada intreaga, fara sa intre
 * sub sageti. Intoarce valoarea de acum daca se vede deja.
 *
 * Cand intrarea e mai lata decat fereastra - „Core Applications Dashboard" pe
 * un ecran de telefon - se aliniaza la STANGA: se citeste inceputul numelui,
 * nu sfarsitul lui.
 */
export function bringIntoView(item: DOMRect, view: DOMRect, scrollLeft: number, pad = ARROW): number {
  const start = view.left + pad
  const end = view.right - pad

  if (item.width >= end - start) return scrollLeft + (item.left - start)
  if (item.right > end) return scrollLeft + (item.right - end)
  if (item.left < start) return scrollLeft - (start - item.left)
  return scrollLeft
}

export interface ModuleBarProps {
  children: ReactNode
  /** Adresa intrarii pe care esti: cand se schimba, ea se aduce in ecran. */
  activeKey?: string
  onMouseEnter?: () => void
  onMouseMove?: () => void
  onMouseLeave?: () => void
}

export function ModuleBar({ children, activeKey, onMouseEnter, onMouseMove, onMouseLeave }: ModuleBarProps) {
  const ref = useRef<HTMLUListElement>(null)
  /* A pus omul mana pe bara? De atunci incolo ea nu se mai misca singura. */
  const owned = useRef(false)
  const [ends, setEnds] = useState<BarEnds>({ left: false, right: false })

  /*
   * Un singur loc care pune bara la locul ei: intai tine in ecran intrarea pe
   * care esti, apoi socoteste ce sageti trebuie aprinse. Se cheama de peste
   * tot - la incarcare, la redimensionare, cand se schimba continutul, cand se
   * deruleaza - fiindca cele doua raspunsuri atarna de aceleasi trei numere.
   *
   * Se aseaza singur: pune `scrollLeft`, de acolo vine un `scroll`, care il
   * cheama din nou, si a doua oara nu mai are ce muta.
   */
  const sync = useCallback(() => {
    const el = ref.current
    if (!el) return

    if (!owned.current) {
      const item = el.querySelector('li.selected')
      if (item) {
        const want = bringIntoView(item.getBoundingClientRect(), el.getBoundingClientRect(), el.scrollLeft)
        if (Math.abs(want - el.scrollLeft) > 1) el.scrollLeft = want
      }
    }

    const next = barEnds(el.scrollLeft, el.scrollWidth, el.clientWidth)
    setEnds((now) => (now.left === next.left && now.right === next.right ? now : next))
  }, [])

  const smooth = useCallback(
    (left: number) => {
      const el = ref.current
      if (!el) return
      const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
      el.scrollTo({ left, behavior: still ? 'auto' : 'smooth' })
    },
    [],
  )

  const nudge = useCallback(
    (dir: -1 | 1) => {
      const el = ref.current
      if (!el) return
      owned.current = true
      smooth(el.scrollLeft + dir * el.clientWidth * STEP)
    },
    [smooth],
  )

  useEffect(() => {
    const el = ref.current
    if (!el) return

    sync()
    el.addEventListener('scroll', sync, { passive: true })

    /*
     * CE SE MASOARA, SI DE CE NU AJUNGE CONTAINERUL.
     *
     * `ResizeObserver` pe bara insasi prinde latimea ferestrei. Atat - si
     * masurat, nu presupus: la prima masuratoare, pe Proactivitee la 420px,
     * bara iesise 1513px, iar dupa ce s-a incarcat fontul 1667. O suta
     * cincizeci si patru de pixeli aparuti fara ca nimic sa se fi schimbat in
     * arbore si fara ca nici cutia containerului sa se miste - deci nici
     * `ResizeObserver` pe el, nici `MutationObserver`, nu spuneau nimic.
     * Sageata din dreapta ramanea stinsa exact peste ce mai era de vazut.
     *
     * De-aia se masoara si FIECARE INTRARE: cand se schimba fontul, sau cand
     * insigna „0" devine „1.243" (cifrele vin cu o cerere separata, dupa
     * bara), intrarea isi schimba latimea si se afla pe loc.
     *
     * `MutationObserver` ramane pentru cate intrari sunt - o intrare stearsa
     * sau adaugata nu e o redimensionare a nimanui - si e cel care aduce la
     * masurat intrarile nou-aparute.
     *
     * Unde nu exista `ResizeObserver` (jsdom) ramane restul: se masoara la
     * derulare, la schimbari in arbore si dupa ce se incarca fontul.
     */
    const size = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => sync())
    const watch = () => {
      if (!size) return
      size.disconnect()
      size.observe(el)
      for (const li of el.children) size.observe(li)
    }
    watch()

    const content = new MutationObserver(() => {
      watch()
      sync()
    })
    content.observe(el, { childList: true, subtree: true, characterData: true })

    /* Si dupa ce fontul chiar a ajuns, inca o data - centura peste bretele. */
    void document.fonts?.ready.then(sync).catch(() => {})

    /* Rotita si degetul inseamna „ma descurc singur". */
    const take = () => {
      owned.current = true
    }
    el.addEventListener('pointerdown', take)

    /*
     * Rotita: pe orizontala, dar numai cat timp bara chiar mai are unde sa
     * mearga in partea aia. La capat nu se opreste nimic si pagina isi
     * continua derularea - bara sta in varful paginii, si o rotita care se
     * blocheaza acolo ar fi un defect mai mare decat cel reparat.
     */
    const wheel = (event: WheelEvent) => {
      if (event.deltaX !== 0 || event.deltaY === 0 || event.ctrlKey) return
      const max = el.scrollWidth - el.clientWidth
      if (max <= 1) return
      const next = el.scrollLeft + event.deltaY
      if (next < 0 || next > max) return
      event.preventDefault()
      owned.current = true
      el.scrollLeft = next
    }
    el.addEventListener('wheel', wheel, { passive: false })

    return () => {
      el.removeEventListener('scroll', sync)
      el.removeEventListener('wheel', wheel)
      el.removeEventListener('pointerdown', take)
      size?.disconnect()
      content.disconnect()
    }
  }, [sync])

  /*
   * Intrarea pe care esti se vede de cum se deschide pagina, si dupa fiecare
   * navigare.
   *
   * `useLayoutEffect`, nu `useEffect`: altfel bara se deseneaza o clipa la
   * inceput si sare apoi, si saritura se vede.
   *
   * Aici se si UITA ce a derulat omul de mana: e alt ecran, deci bara se aseaza
   * din nou dupa el. Cat timp sta pe acelasi ecran, ce a derulat singur ramane
   * cum l-a lasat - nici fontul, nici o insigna intarziata nu i-o mai misca.
   */
  useLayoutEffect(() => {
    owned.current = false
    sync()
  }, [activeKey, sync])

  return (
    <ul
      id="page-header-nav"
      ref={ref}
      onMouseEnter={onMouseEnter}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
    >
      {/*
        Sagetile stau MEREU in markup, stinse cu `hidden`, fiindca `:first-child`
        din `app.css` se uita la arbore, nu la ce se vede: scoase si puse la loc,
        marginea de 20px a primei intrari ar juca. Vezi ModuleBar.css.
      */}
      <li className="od-bar-arrow od-bar-arrow-left menu-space" hidden={!ends.left} aria-hidden="true">
        <button type="button" tabIndex={-1} title="Scroll left" onClick={() => nudge(-1)}>
          <i className="fas fa-chevron-left"></i>
        </button>
      </li>
      {children}
      <li className="od-bar-arrow od-bar-arrow-right menu-space" hidden={!ends.right} aria-hidden="true">
        <button type="button" tabIndex={-1} title="Scroll right" onClick={() => nudge(1)}>
          <i className="fas fa-chevron-right"></i>
        </button>
      </li>
    </ul>
  )
}
